import React, { useEffect, useState } from "react";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { KeyRound, Mail, Wallet, ShieldCheck, X } from "lucide-react";

export default function ChangeMyPassword({ onClose, forceSetup }) {
  const { user, refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [profile, setProfile] = useState({ recovery_email: "", bank_account_name: "", bank_bsb: "", bank_account_number: "", pay_id: "" });
  const [loadingPw, setLoadingPw] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [backup, setBackup] = useState(null);
  const [freshBackup, setFreshBackup] = useState(null);

  useEffect(() => {
    api.get("/admin/me/profile").then(r => setProfile({
      recovery_email: r.data?.recovery_email || "",
      bank_account_name: r.data?.bank_account_name || "",
      bank_bsb: r.data?.bank_bsb || "",
      bank_account_number: r.data?.bank_account_number || "",
      pay_id: r.data?.pay_id || "",
    })).catch(() => {});
    api.get("/admin/me/backup-credentials").then(r => setBackup(r.data)).catch(() => {});
  }, []);

  const regenerateBackup = async () => {
    const confirmed = window.confirm("Regenerate your personal backup code? Your existing backup password will stop working immediately. The new password will only be shown once — copy it before closing.");
    if (!confirmed) return;
    try {
      const r = await api.post("/admin/me/backup-credentials/regenerate");
      setFreshBackup(r.data);
      setBackup({ backup_identifier: r.data.backup_identifier, has_backup_password: true, backup_password_set_at: new Date().toISOString() });
      toast.success("New backup code generated — copy it now");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const isSuper = user?.role === "super_admin";

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setLoadingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated");
      setCur(""); setPw(""); setPw2("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoadingPw(false); }
  };

  const saveProfile = async () => {
    setLoadingProfile(true);
    try {
      await api.put("/admin/me/profile", profile);
      toast.success("Saved");
      if (refresh) await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoadingProfile(false); }
  };

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/95 z-[100] grid place-items-center p-6 overflow-y-auto" onClick={forceSetup ? undefined : onClose}>
      <div className="card-surface p-8 max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="font-display text-3xl uppercase">{forceSetup ? "Finish setting up" : "My account"}</h2>
            {forceSetup && <p className="text-amber-400 text-xs mt-1 uppercase tracking-widest">Required — one-time setup</p>}
          </div>
          {!forceSetup && <button type="button" onClick={onClose} data-testid="my-account-close"><X size={20}/></button>}
        </div>
        <p className="text-zinc-400 text-sm mb-6">Signed in as <strong>{user?.email}</strong>{forceSetup && <><br/><span className="text-zinc-500">That's a username — please add your <strong>real</strong> personal email below and change your default password. This unlocks self-service password reset.</span></>}</p>

        {/* Recovery email */}
        <div className="mb-6 border-b border-white/10 pb-6">
          <div className="eyebrow mb-2 flex items-center gap-2"><Mail size={12}/>Recovery email</div>
          <p className="text-xs text-zinc-500 mb-3">Password reset links land here. Blank = your login email.</p>
          <input type="email" value={profile.recovery_email} onChange={e => set("recovery_email", e.target.value)} placeholder="you@personal.com" required={forceSetup} className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm focus:border-red-600 outline-none" data-testid="recovery-email-input"/>
          {forceSetup && (
            <button onClick={saveProfile} disabled={loadingProfile || !profile.recovery_email} className="btn-red w-full text-sm disabled:opacity-40" data-testid="force-setup-save-email">
              {loadingProfile ? "Saving…" : "Save recovery email & continue"}
            </button>
          )}
        </div>

        {/* Personal backup access code — super_admin only */}
        {isSuper && (
          <div className="mb-6 border-b border-white/10 pb-6" data-testid="backup-creds-panel">
            <div className="eyebrow mb-2 flex items-center gap-2"><ShieldCheck size={12}/>Personal backup access</div>
            <p className="text-xs text-zinc-500 mb-3">If you ever forget your email or password, log in with your personal backup code (unique to you — not shared with other super admins).</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Your backup identifier</div>
                <div className="font-mono text-lg text-white bg-black border border-white/10 px-3 py-2" data-testid="backup-identifier">{backup?.backup_identifier || "…"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Password status</div>
                <div className={`text-sm px-3 py-2 border ${backup?.has_backup_password ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                  {backup?.has_backup_password ? `Set on ${(backup.backup_password_set_at || "").slice(0,10)}` : "Not set — regenerate to activate"}
                </div>
              </div>
            </div>
            {freshBackup && (
              <div className="border border-emerald-500/40 bg-emerald-500/5 p-4 mb-3" data-testid="fresh-backup-block">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Copy now — will not be shown again</div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-white">Identifier: <strong>{freshBackup.backup_identifier}</strong></div>
                  <button onClick={() => navigator.clipboard?.writeText(freshBackup.backup_identifier)} className="text-[10px] text-zinc-400 hover:text-white">Copy</button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-white">Password: <strong>{freshBackup.backup_password}</strong></div>
                  <button onClick={() => navigator.clipboard?.writeText(freshBackup.backup_password)} className="text-[10px] text-zinc-400 hover:text-white">Copy</button>
                </div>
              </div>
            )}
            <button onClick={regenerateBackup} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="regen-backup">
              {backup?.has_backup_password ? "Regenerate backup code" : "Generate backup password"}
            </button>
          </div>
        )}

        {/* Bank / payment details */}
        <div className="mb-6 border-b border-white/10 pb-6">
          <div className="eyebrow mb-2 flex items-center gap-2"><Wallet size={12}/>Where should we pay you</div>
          <p className="text-xs text-zinc-500 mb-3">Only the manager + super admin can see these on your payroll line. Fill either bank details or PayID (or both).</p>
          <div className="grid grid-cols-2 gap-3">
            <input value={profile.bank_account_name} onChange={e => set("bank_account_name", e.target.value)} placeholder="Account name" className="col-span-2 bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="bank-account-name"/>
            <input value={profile.bank_bsb} onChange={e => set("bank_bsb", e.target.value)} placeholder="BSB (e.g. 083-081)" className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="bank-bsb"/>
            <input value={profile.bank_account_number} onChange={e => set("bank_account_number", e.target.value)} placeholder="Account number" className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="bank-account-number"/>
            <input value={profile.pay_id} onChange={e => set("pay_id", e.target.value)} placeholder="PayID (email or phone)" className="col-span-2 bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="pay-id"/>
          </div>
          <button onClick={saveProfile} disabled={loadingProfile} className="btn-ghost !py-2 !px-3 !text-xs mt-3" data-testid="save-profile">
            {loadingProfile ? "Saving…" : "Save details"}
          </button>
        </div>

        {/* Password change */}
        <form onSubmit={submitPw}>
          <div className="eyebrow mb-3 flex items-center gap-2"><KeyRound size={12}/>Change password</div>
          <input type="password" placeholder="Current password" value={cur} onChange={e => setCur(e.target.value)}
            className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm" required data-testid="cur-pw"/>
          <input type="password" placeholder="New password (min 8 chars)" value={pw} onChange={e => setPw(e.target.value)}
            className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm" required data-testid="new-pw"/>
          <input type="password" placeholder="Confirm new password" value={pw2} onChange={e => setPw2(e.target.value)}
            className="w-full bg-black border border-white/10 px-3 py-2 mb-4 text-sm" required data-testid="new-pw2"/>
          <button type="submit" disabled={loadingPw} className="btn-red w-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-change-pw">
            <KeyRound size={14}/>{loadingPw ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
